import type { DrawTool } from '@kinop/shared';
import type { BrushSettings } from './DrawCanvas.js';

interface DrawToolbarProps {
  brush: BrushSettings;
  onChange: (patch: Partial<BrushSettings>) => void;
  onUndo: () => void;
}

const TOOLS: { id: DrawTool; label: string }[] = [
  { id: 'brush', label: '✎ Кисть' },
  { id: 'spray', label: '🎨 Баллончик' },
  { id: 'stamp', label: '💠 Фигуры' },
];

/** Те же цвета, что у аватарок: комната остаётся в одной палитре. */
const COLORS = ['#ff5fa2', '#e8ff59', '#7ed9c4', '#a97bff', '#ff8a4c', '#f2f0f4'];

/** Толщина в долях ширины кадра, а не в пикселях. */
const SIZES = [0.004, 0.008, 0.016];

const STAMPS = ['❤️', '💩', '⭐️', '🔥', '💀', '🤡', '👑', '🍆', '💸', '👀', '🫶', '🎯'];

export function DrawToolbar({ brush, onChange, onUndo }: DrawToolbarProps): JSX.Element {
  return (
    <div className="draw-tools">
      <div className="draw-tools__grid">
        <div>
          <span className="mono-label">Инструмент</span>
          <div className="draw-tools__row">
            {TOOLS.map((tool) => (
              <button
                key={tool.id}
                type="button"
                className="chip"
                data-on={brush.tool === tool.id && !brush.eraser ? '' : undefined}
                aria-pressed={brush.tool === tool.id && !brush.eraser}
                onClick={() => onChange({ tool: tool.id, eraser: false })}
              >
                {tool.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <span className="mono-label">Цвет и толщина</span>
          <div className="draw-tools__row">
            {COLORS.map((color) => (
              <button
                key={color}
                type="button"
                className="swatch"
                style={{ background: color }}
                data-on={brush.color === color && !brush.eraser ? '' : undefined}
                aria-label={`Цвет ${color}`}
                aria-pressed={brush.color === color && !brush.eraser}
                onClick={() => onChange({ color, eraser: false })}
              />
            ))}
            <span className="draw-tools__sep" aria-hidden="true" />
            {SIZES.map((size, index) => (
              <button
                key={size}
                type="button"
                className="size-btn"
                data-on={brush.size === size ? '' : undefined}
                aria-label={['Тонкая линия', 'Средняя линия', 'Толстая линия'][index]}
                aria-pressed={brush.size === size}
                onClick={() => onChange({ size })}
              >
                <span style={{ width: 4 + index * 5, height: 4 + index * 5 }} />
              </button>
            ))}
          </div>
        </div>

        <div>
          <span className="mono-label">Ещё</span>
          <div className="draw-tools__row">
            <button
              type="button"
              className="chip"
              data-on={brush.neon ? '' : undefined}
              aria-pressed={brush.neon}
              onClick={() => onChange({ neon: !brush.neon })}
            >
              ✦ Неон
            </button>
            <button
              type="button"
              className="chip"
              data-on={brush.eraser ? '' : undefined}
              aria-pressed={brush.eraser}
              onClick={() => onChange({ eraser: !brush.eraser })}
            >
              ◻ Ластик
            </button>
            <button type="button" className="chip" onClick={onUndo}>
              ↩ Отменить
            </button>
          </div>
        </div>
      </div>

      {brush.tool === 'stamp' && !brush.eraser && (
        <div>
          <span className="mono-label">Готовые формы · нажатие по кадру ставит штамп</span>
          <div className="draw-tools__row">
            {STAMPS.map((char) => (
              <button
                key={char}
                type="button"
                className="stamp-btn"
                data-on={brush.stamp === char ? '' : undefined}
                aria-label={`Штамп ${char}`}
                aria-pressed={brush.stamp === char}
                onClick={() => onChange({ stamp: char, tool: 'stamp', eraser: false })}
              >
                {char}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
